import { useFormikContext } from "formik";
import React, { FC, useEffect, useState } from "react";
import { DEF_PG_SIZE_WIZARD } from "../../../../constants/maximum-page-size";
import { MAX_RECORDS } from "../../../../constants/notification-key";
import { useLocale } from "../../../../contexts/localeContext";
import { TableContextProvider } from "../../../../contexts/table/TableContextProvider";
import { TranslationKeys } from "../../../../contexts/TranslationKeys";
import SiteColumns from "../../../../models/ColumnDefinitions/SiteColumns";
import { ListData } from "../../../../models/ListData";
import { ListQuery } from "../../../../models/ListQuery";
import { Lookup } from "../../../../models/Lookup";
import LookupTypes from "../../../../models/LookupTypes";
import SiteSearch from "../../../../models/search/SiteSearch";
import TimeZoneSearch from "../../../../models/search/TimeZoneSearch";
import { getSites } from "../../../../services/siteService";
import { getTimeZones } from "../../../../services/timeZoneService";
import { appendFilter } from "../../../../utilities/filters";
import AssignWizard from "../../../assignWizard/AssignWizard";
import TableTab from "../../../tabs/table-tab";
import { toast } from "react-toastify";

interface FilterBySiteProps {
    manageItems: (selectIds: string[], oldSelectedIds: string[]) => void;
}

const FilterBySite: FC<FilterBySiteProps> = ({ manageItems }) => {
    const { translate } = useLocale();
    const [showManagedModal, setShowManagedModal] = useState(false);
    const [dataRefresh, setDataRefresh] = useState(() => (dataMethod?: (query?: ListQuery) => Promise<ListData>) => { return });
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [oldSelectedIds, setOldSelectedIds] = useState<string[]>([]);
    const { values } = useFormikContext<any>();

    useEffect(() => {
        setSelectedIds(values.filterIds);
        setOldSelectedIds(values.filterIds);
        dataRefresh(getDataMethod());
    }, [values.filterIds])

    useEffect(() => {
        setSelectedIds(values.filterIds);
        setOldSelectedIds(values.filterIds);
    }, [showManagedModal])

    const lookups: Lookup[] = [
        {
            name: LookupTypes.timeZone,
            values: [],
            dataMethod: getTimeZones,
            searchAccessor: TimeZoneSearch.find(item => item.accessor === "name")?.searchOnName
        }
    ];

    const getDataMethod = (): (listParameter?: ListQuery) => Promise<ListData> => {
        if (values.filterIds && values.filterIds.length) {
            return (listParameters?: ListQuery) => getSites({
                ...listParameters, filters: appendFilter(
                    values.filterIds?.toString(),
                    "Items",
                    "id",
                    listParameters?.filters ?? [],
                    "=in="
                )
            })
        }

        return () => {
            return new Promise((resolve) => {
                resolve({ rows: [], pagination: { maxPageSize: 0, pageNumber: 1, totalCount: 0, totalPages: 0 } })
            })
        }
    }

    const manualDataRefresh = (refreshData: (dataMethod?: (query?: ListQuery) => Promise<ListData>) => void) => {
        setDataRefresh(() => refreshData);
    }

    const onSelectedIds = (ids: string[]) => {
        if (ids.length >= MAX_RECORDS) {
            toast.error(translate.byKey(TranslationKeys.aMaximimumOf20RecordsCanBeSelected));
            setSelectedIds([...selectedIds])
        } else {
            setSelectedIds([...ids])
        }
    }

    return <>
        <TableContextProvider
            dataMethod={getDataMethod()}
            useTable
            usePagination
            useFilter
            useSearch
            useSort
            tableDescriptor={SiteColumns}
            searchDescriptor={SiteSearch}
            maxPageSize={DEF_PG_SIZE_WIZARD}
            manualDataRefresh={manualDataRefresh}
            lookup={lookups}
            canExportReport={false}>
            <TableTab
                title={translate.byKey(TranslationKeys.site)}
                selectRows={false}
                tableId={'filterBySiteTable'}
                cyName="filterBySite">
                <button
                    type="button"
                    className="flex px-4 bg-lime rounded-lg shadow text-white text-sm items-center"
                    onClick={() => setShowManagedModal(true)}>
                    {translate.byKey(TranslationKeys.manage)}
                </button>
            </TableTab>
        </TableContextProvider>
        <AssignWizard
            isOpen={showManagedModal}
            onClose={() => setShowManagedModal(false)}
            title={translate.byKey(TranslationKeys.site)}
            lookup={lookups}
            dataMethod={getSites}
            columnDescriptors={{
                columns: SiteColumns,
                searchColumns: SiteSearch
            }}
            handleOnSubmit={() => manageItems(selectedIds, oldSelectedIds)}
            selectedIds={selectedIds}
            setSelectedIds={onSelectedIds} />
    </>
}

export default FilterBySite;
